<template>
  <div class="p-3 md:p-6 bg-slate-50 dark:bg-gray-900 min-h-screen font-sans text-slate-800 dark:text-slate-100">
    <!-- Encabezado -->
    <div class="flex flex-col md:flex-row justify-between items-start md:items-center mb-6 gap-4 bg-white dark:bg-gray-800 p-4 rounded-2xl shadow-sm border border-slate-100 dark:border-gray-700">
      <div class="flex items-center space-x-3">
        <div class="h-10 w-1.5 bg-gradient-to-b from-indigo-500 to-purple-600 rounded-full"></div>
        <div>
          <h1 class="text-xl md:text-2xl font-bold text-gray-800 dark:text-white flex items-center gap-2">
            <Icon icon="mdi:tools" class="text-indigo-600 dark:text-indigo-400" />
            Nueva Salida de Herramientas
          </h1>
          <p class="text-xs text-gray-500 dark:text-gray-400 mt-1 flex items-center">
            <Icon icon="mdi:swap-vertical-variant" class="mr-1 text-indigo-500" />
            Registro de retiro de herramientas para técnicos
          </p>
        </div>
      </div>
      <button 
        @click="$router.push('/salida-herramienta')"
        class="bg-white dark:bg-gray-800 border border-slate-200 dark:border-gray-700 text-slate-600 dark:text-gray-300 px-4 py-2 rounded-xl font-bold transition-all hover:bg-slate-50 dark:hover:bg-gray-700 flex items-center gap-2"
      >
        <Icon icon="mdi:arrow-left" class="text-lg" />
        Volver al Listado
      </button>
    </div>

    <!-- Formulario en dos columnas -->
    <div class="grid grid-cols-1 lg:grid-cols-2 gap-6">
      
      <!-- Columna Izquierda: Información del Técnico y Datos Generales -->
      <div class="space-y-6">
        
        <!-- Tarjeta de Técnico -->
        <div class="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-slate-100 dark:border-gray-700 text-slate-800 dark:text-slate-100">
          <div class="p-4 border-b border-slate-50 dark:border-gray-700 bg-slate-50/50 dark:bg-gray-700/30 rounded-t-2xl">
            <h2 class="text-sm font-black text-slate-700 dark:text-white uppercase tracking-wider flex items-center gap-2">
              <Icon icon="mdi:account-wrench" class="text-indigo-600" />
              Técnico Responsable
            </h2>
          </div>
          
          <div class="p-6">
            <!-- Buscador de Técnico -->
            <div v-if="!tecnicoSeleccionado" class="relative">
              <label class="block text-[10px] font-black text-slate-400 uppercase mb-2 ml-1">Buscar Técnico <span class="text-indigo-500">({{ tecnicos.length }} disponibles)</span></label>
              <div class="relative">
                <Icon icon="mdi:magnify" class="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
                <input 
                  v-model="buscarTecnico"
                  type="text" 
                  placeholder="Nombre o usuario del técnico..." 
                  class="w-full pl-12 pr-4 py-3 bg-slate-50 dark:bg-gray-700 border-none rounded-xl focus:ring-2 focus:ring-indigo-500 transition-all text-sm text-slate-700 dark:text-white font-bold"
                  @focus="mostrarDropdownTecnicos = true"
                  @blur="ocultarDropdownTecnicos"
                />
              </div>

              <!-- Resultados de búsqueda -->
              <div v-if="mostrarDropdownTecnicos && tecnicosFiltrados.length > 0" class="absolute z-10 w-full mt-2 bg-white dark:bg-gray-800 border border-slate-100 dark:border-gray-700 rounded-xl shadow-xl max-h-60 overflow-y-auto">
                <div 
                  v-for="t in tecnicosFiltrados" 
                  :key="t.idUsuario"
                  @click="seleccionarTecnico(t)"
                  class="p-3 hover:bg-slate-50 dark:hover:bg-gray-700 cursor-pointer flex items-center gap-3 border-b border-slate-50 dark:border-gray-700 last:border-0"
                >
                  <div class="h-8 w-8 bg-indigo-100 dark:bg-indigo-900/30 text-indigo-600 rounded-full flex items-center justify-center font-black text-xs">
                    {{ (t.nombre || t.nombreUsuario || t.usuario || 'U').substring(0, 2).toUpperCase() }}
                  </div>
                  <div>
                    <div class="text-sm font-bold text-slate-700 dark:text-white">
                      <template v-if="t.nombre || t.apellido">
                        {{ t.nombre }} {{ t.apellido }}
                      </template>
                      <template v-else>
                        {{ t.nombreUsuario || t.usuario || 'Técnico Sin Nombre' }}
                      </template>
                    </div>
                    <div class="text-[10px] text-slate-400 uppercase font-black">
                      <span v-if="t.nombreUsuario || t.usuario" class="mr-1">@{{ t.nombreUsuario || t.usuario }}</span>
                      <span v-if="t.especialidad || t.rol">- {{ t.especialidad || t.rol }}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Técnico Seleccionado -->
            <div v-else class="flex items-center justify-between p-4 bg-indigo-50/50 dark:bg-indigo-900/20 rounded-2xl border border-indigo-100 dark:border-indigo-800/30">
              <div class="flex items-center gap-4">
                <div class="h-12 w-12 bg-indigo-600 text-white rounded-2xl flex items-center justify-center font-black text-lg shadow-lg">
                  {{ (tecnicoSeleccionado.nombre || tecnicoSeleccionado.nombreUsuario || tecnicoSeleccionado.usuario || 'U').substring(0, 2).toUpperCase() }}
                </div>
                <div>
                  <div class="text-base font-black text-indigo-900 dark:text-indigo-100">
                    <template v-if="tecnicoSeleccionado.nombre || tecnicoSeleccionado.apellido">
                      {{ tecnicoSeleccionado.nombre }} {{ tecnicoSeleccionado.apellido }}
                    </template>
                    <template v-else>
                      {{ tecnicoSeleccionado.nombreUsuario || tecnicoSeleccionado.usuario || 'Técnico Sin Nombre' }}
                    </template>
                  </div>
                  <div class="text-[10px] text-indigo-600 dark:text-indigo-400 uppercase font-black tracking-widest">
                    <span v-if="tecnicoSeleccionado.nombreUsuario || tecnicoSeleccionado.usuario" class="mr-1">@{{ tecnicoSeleccionado.nombreUsuario || tecnicoSeleccionado.usuario }} -</span>
                    Responsable del Retiro
                  </div>
                </div>
              </div>
              <button @click="tecnicoSeleccionado = null" class="p-2 text-rose-500 hover:bg-rose-50 dark:hover:bg-rose-900/30 rounded-lg transition-colors">
                <Icon icon="mdi:close-circle" class="text-2xl" />
              </button>
            </div>
          </div>
        </div>

        <!-- Datos Generales -->
        <div class="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-slate-100 dark:border-gray-700 overflow-hidden text-slate-800 dark:text-slate-100">
          <div class="p-4 border-b border-slate-50 dark:border-gray-700 bg-slate-50/50 dark:bg-gray-700/30">
            <h2 class="text-sm font-black text-slate-700 dark:text-white uppercase tracking-wider flex items-center gap-2">
              <Icon icon="mdi:clipboard-text-outline" class="text-indigo-600" />
              Detalles de la Salida
            </h2>
          </div>
          <div class="p-6 space-y-4 text-slate-800 dark:text-slate-100">
            <div class="grid grid-cols-2 gap-4">
              <div>
                <label class="block text-[10px] font-black text-slate-400 uppercase mb-2 ml-1">Código de Referencia</label>
                <div class="relative">
                  <Icon icon="mdi:barcode-scan" class="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
                  <input 
                    v-model="form.codigoSalida"
                    type="text" 
                    placeholder="SAL-001" 
                    class="w-full pl-12 pr-4 py-3 bg-slate-50 dark:bg-gray-700 border-none rounded-xl focus:ring-2 focus:ring-indigo-500 transition-all text-sm text-slate-700 dark:text-white font-bold"
                  />
                </div>
              </div>
              <div>
                <label class="block text-[10px] font-black text-slate-400 uppercase mb-2 ml-1">Fecha de Salida</label>
                <div class="relative">
                  <Icon icon="mdi:calendar" class="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
                  <input 
                    v-model="form.fechaSalida"
                    type="datetime-local" 
                    class="w-full pl-12 pr-4 py-3 bg-slate-50 dark:bg-gray-700 border-none rounded-xl focus:ring-2 focus:ring-indigo-500 transition-all text-sm text-slate-700 dark:text-white font-bold"
                  />
                </div>
              </div>
            </div>

            <div>
              <label class="block text-[10px] font-black text-slate-400 uppercase mb-2 ml-1">Motivo / Destino</label>
              <div class="relative">
                <Icon icon="mdi:map-marker" class="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
                <input 
                  v-model="form.motivo"
                  type="text" 
                  placeholder="Ej: Reparación en domicilio, Trabajo en taller externo..." 
                  class="w-full pl-12 pr-4 py-3 bg-slate-50 dark:bg-gray-700 border-none rounded-xl focus:ring-2 focus:ring-indigo-500 transition-all text-sm text-slate-700 dark:text-white font-bold"
                />
              </div>
            </div>

            <div>
              <label class="block text-[10px] font-black text-slate-400 uppercase mb-2 ml-1">Observaciones</label>
              <textarea 
                v-model="form.observaciones"
                rows="3" 
                placeholder="Detalles adicionales sobre el estado de las herramientas o la tarea..." 
                class="w-full p-4 bg-slate-50 dark:bg-gray-700 border-none rounded-xl focus:ring-2 focus:ring-indigo-500 transition-all text-sm text-slate-700 dark:text-white font-bold resize-none"
              ></textarea>
            </div>
          </div>
        </div>
      </div>

      <!-- Columna Derecha: Selección de Herramientas y Carrito -->
      <div class="space-y-6">
        
        <!-- Buscador de Herramientas -->
        <div class="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-slate-100 dark:border-gray-700 overflow-hidden text-slate-800 dark:text-slate-100">
          <div class="p-4 border-b border-slate-50 dark:border-gray-700 bg-slate-50/50 dark:bg-gray-700/30">
            <h2 class="text-sm font-black text-slate-700 dark:text-white uppercase tracking-wider flex items-center gap-2">
              <Icon icon="mdi:magnify" class="text-indigo-600" />
              Seleccionar Herramientas
            </h2>
          </div>
          <div class="p-6">
            <div class="relative mb-6">
              <Icon icon="mdi:wrench" class="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
              <input 
                v-model="buscarHerramienta"
                type="text" 
                placeholder="Buscar por nombre, código o marca..." 
                class="w-full pl-12 pr-12 py-3 bg-slate-50 dark:bg-gray-700 border-none rounded-xl focus:ring-2 focus:ring-indigo-500 transition-all text-sm text-slate-700 dark:text-white font-bold"
              />
              <span class="absolute right-4 top-1/2 -translate-y-1/2 text-[10px] font-black text-indigo-500 bg-indigo-50 dark:bg-indigo-900/30 px-2 py-1 rounded-md">{{ herramientas.length }}</span>
            </div>

            <!-- Listado de Herramientas Disponibles -->
            <div class="grid grid-cols-1 md:grid-cols-2 gap-3 max-h-[300px] overflow-y-auto pr-2 custom-scrollbar">
              <div 
                v-for="h in herramientasFiltradas" 
                :key="h.idHerramienta"
                @click="agregarAlCarrito(h)"
                class="p-3 border border-slate-100 dark:border-gray-700 rounded-xl hover:border-indigo-500 dark:hover:border-indigo-400 transition-all cursor-pointer group bg-white dark:bg-gray-800"
              >
                <div class="flex justify-between items-start mb-1">
                  <div class="text-xs font-black text-indigo-600 dark:text-indigo-400 uppercase tracking-tighter">{{ h.codigo }}</div>
                  <div class="text-[9px] px-1.5 py-0.5 bg-slate-100 dark:bg-gray-700 rounded font-black text-slate-500">{{ h.marca || 'Genérica' }}</div>
                </div>
                <div class="text-sm font-bold text-slate-700 dark:text-white group-hover:text-indigo-600 transition-colors">{{ h.nombre }}</div>
                <div class="flex justify-between items-center mt-2">
                  <div class="text-[10px] font-bold text-slate-400 uppercase">Stock: <span :class="h.stock > 0 ? 'text-emerald-500' : 'text-rose-500'">{{ h.stock }}</span></div>
                  <Icon icon="mdi:plus-circle" class="text-indigo-500 opacity-0 group-hover:opacity-100 transition-all transform scale-75 group-hover:scale-100" />
                </div>
              </div>
              <div v-if="herramientasFiltradas.length === 0 && buscarHerramienta" class="col-span-2 py-8 text-center">
                <Icon icon="mdi:package-variant-closed" class="text-3xl text-slate-200 dark:text-gray-700 mx-auto mb-2" />
                <p class="text-[10px] font-black text-slate-400 uppercase">No se encontraron herramientas</p>
              </div>
              <div v-if="herramientasFiltradas.length === 0 && !buscarHerramienta" class="col-span-2 py-8 text-center">
                <Icon icon="mdi:magnify" class="text-3xl text-slate-200 dark:text-gray-700 mx-auto mb-2" />
                <p class="text-[10px] font-black text-slate-400 uppercase">Escribe para buscar herramientas</p>
              </div>
            </div>
          </div>
        </div>

        <!-- Carrito de Herramientas -->
        <div class="bg-white dark:bg-gray-800 rounded-2xl shadow-xl border border-slate-100 dark:border-gray-700 overflow-hidden text-slate-800 dark:text-slate-100">
          <div class="p-4 border-b border-slate-50 dark:border-gray-700 bg-indigo-600 flex justify-between items-center">
            <h2 class="text-sm font-black text-white uppercase tracking-wider flex items-center gap-2">
              <Icon icon="mdi:cart-arrow-down" />
              Herramientas en la Salida
            </h2>
            <span class="px-2 py-1 bg-white/20 text-white rounded text-[10px] font-black">{{ carrito.length }} ITEMS</span>
          </div>

          <div class="p-6">
            <div v-if="carrito.length > 0" class="space-y-3">
              <div 
                v-for="(item, index) in carrito" 
                :key="item.idHerramienta"
                class="flex items-center gap-4 p-3 bg-slate-50 dark:bg-gray-700/50 rounded-2xl border border-slate-100 dark:border-gray-600 group"
              >
                <div class="h-10 w-10 bg-white dark:bg-gray-800 rounded-xl flex items-center justify-center text-indigo-600 shadow-sm border border-slate-100 dark:border-gray-700">
                  <Icon icon="mdi:wrench" class="text-xl" />
                </div>
                <div class="flex-1 min-w-0">
                  <div class="text-sm font-black text-slate-700 dark:text-white truncate">{{ item.nombre }}</div>
                  <div class="text-[10px] text-slate-400 font-bold uppercase tracking-tighter">{{ item.codigo }}</div>
                </div>
                <div class="flex items-center gap-2 bg-white dark:bg-gray-800 p-1 rounded-lg border border-slate-100 dark:border-gray-700">
                  <button @click="disminuirCantidad(index)" class="p-1 hover:bg-slate-50 dark:hover:bg-gray-700 text-slate-400 hover:text-indigo-600 rounded">
                    <Icon icon="mdi:minus" />
                  </button>
                  <input 
                    v-model.number="item.cantidad"
                    type="number" 
                    min="1" 
                    :max="item.stock"
                    class="w-8 text-center bg-transparent border-none text-xs font-black text-slate-700 dark:text-white focus:ring-0 p-0"
                  />
                  <button @click="aumentarCantidad(index)" class="p-1 hover:bg-slate-50 dark:hover:bg-gray-700 text-slate-400 hover:text-indigo-600 rounded">
                    <Icon icon="mdi:plus" />
                  </button>
                </div>
                <button @click="removerDelCarrito(index)" class="p-2 text-rose-500 hover:bg-rose-50 dark:hover:bg-rose-900/30 rounded-lg opacity-0 group-hover:opacity-100 transition-all">
                  <Icon icon="mdi:trash-can" class="text-lg" />
                </button>
              </div>

              <div class="pt-6 border-t border-slate-100 dark:border-gray-700 space-y-4">
                <button 
                  @click="procesarSalida"
                  :disabled="!esValido || guardando"
                  class="w-full py-4 bg-indigo-600 hover:bg-indigo-700 disabled:bg-slate-200 dark:disabled:bg-gray-700 text-white rounded-2xl font-black text-sm uppercase tracking-widest shadow-xl shadow-indigo-100 dark:shadow-none transition-all flex items-center justify-center gap-2"
                >
                  <Icon v-if="guardando" icon="svg-spinners:ring-resize" class="text-lg" />
                  <Icon v-else icon="mdi:check-circle" class="text-lg" />
                  {{ guardando ? 'Registrando Salida...' : 'Confirmar Registro de Salida' }}
                </button>
                <div v-if="!esValido" class="text-center">
                  <p class="text-[9px] font-black text-rose-500 uppercase flex items-center justify-center gap-1">
                    <Icon icon="mdi:alert-circle" />
                    {{ errorValidacion }}
                  </p>
                </div>
              </div>
            </div>

            <div v-else class="py-12 text-center">
              <div class="h-20 w-20 bg-slate-50 dark:bg-gray-700/50 rounded-full flex items-center justify-center mx-auto mb-4">
                <Icon icon="mdi:toolbox-outline" class="text-4xl text-slate-200 dark:text-gray-600" />
              </div>
              <p class="text-xs font-black text-slate-400 uppercase">La lista está vacía</p>
              <p class="text-[10px] text-slate-300 font-bold mt-1">Busca y selecciona herramientas para continuar</p>
            </div>
          </div>
        </div>
      </div>

    </div>

    <!-- Custom Toast -->
    <Teleport to="body">
      <div v-if="toastVisible" 
           class="fixed top-4 right-4 z-50 flex items-center px-4 py-3 rounded-2xl shadow-xl font-bold transition-all duration-300 transform translate-y-0 opacity-100"
           :class="toastType === 'error' ? 'bg-red-500 text-white' : (toastType === 'warning' ? 'bg-amber-500 text-white' : 'bg-green-500 text-white')">
        <Icon :icon="toastType === 'error' ? 'mdi:alert-circle' : (toastType === 'warning' ? 'mdi:alert' : 'mdi:check-circle')" class="mr-2 text-xl" />
        <span class="text-sm">{{ toastMessage }}</span>
      </div>
    </Teleport>

    <!-- Custom Success Modal -->
    <Teleport to="body">
      <div v-if="showSuccessModal" class="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
        <div class="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-sm p-6 text-center animate-fadeIn">
          <div class="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-green-100 dark:bg-green-900/30 mb-4">
            <Icon icon="mdi:check-circle" class="text-4xl text-green-600 dark:text-green-400" />
          </div>
          <h2 class="text-xl font-bold text-gray-800 dark:text-white mb-2">¡Registro Exitoso!</h2>
          <p class="text-sm text-gray-600 dark:text-gray-300 mb-6">La salida de herramientas se ha registrado correctamente.</p>
          <button @click="onSuccessModalClose" class="w-full py-2.5 bg-indigo-600 text-white rounded-xl font-bold hover:bg-indigo-700 transition-colors">
            Aceptar
          </button>
        </div>
      </div>
    </Teleport>
  </div>
</template>

<script setup>
import { ref, reactive, onMounted, computed } from 'vue';
import { useRouter } from 'vue-router';
import { Icon } from '@iconify/vue';
import { obtenerUsuarios } from '@/api/usuarios';
import { obtenerTecnicos } from '@/api/tecnicos';
import { getHerramientas } from '@/api/herramientas';
import { crearSalidaHerramienta } from '@/api/salidaHerramienta';
import { getCurrentUser } from '@/api/index';

const router = useRouter();
const user = getCurrentUser();

// State
const tecnicos = ref([]);
const herramientas = ref([]);
const buscarTecnico = ref('');
const buscarHerramienta = ref('');
const tecnicoSeleccionado = ref(null);
const carrito = ref([]);
const guardando = ref(false);
const mostrarDropdownTecnicos = ref(false);

const showSuccessModal = ref(false);
const toastVisible = ref(false);
const toastMessage = ref('');
const toastType = ref('success');
let toastTimer = null;

const form = reactive({
  codigoSalida: `SAL-${new Date().toISOString().slice(2, 10).replace(/-/g, '')}-${Math.floor(Math.random() * 1000).toString().padStart(3, '0')}`,
  fechaSalida: new Date().toISOString().slice(0, 16),
  motivo: '',
  observaciones: '',
  idUsuarioEntrega: user?.idUsuario || user?.id || 1,
  idUsuarioRecibe: null,
  estado: 'prestado'
});

// Computed
const tecnicosFiltrados = computed(() => {
  const q = String(buscarTecnico.value || '').toLowerCase();
  const baseTecnicos = (tecnicos.value || []).filter(t => t.idUsuario);
  if (!q) {
    return baseTecnicos.slice(0, 10);
  }
  return baseTecnicos.filter(t => 
    (t?.nombreUsuario || t?.usuario || '').toLowerCase().includes(q) ||
    (t?.nombre || '').toLowerCase().includes(q) ||
    (t?.apellido || '').toLowerCase().includes(q)
  ).slice(0, 10);
});

const herramientasFiltradas = computed(() => {
  const q = String(buscarHerramienta.value || '').toLowerCase();
  if (!q) {
    return []; // A petición del usuario, no mostrar hasta que se escriba
  }
  return (herramientas.value || []).filter(h => 
    (h?.nombre || '').toLowerCase().includes(q) ||
    (h?.codigo || '').toLowerCase().includes(q) ||
    (h?.marca || '').toLowerCase().includes(q)
  ).slice(0, 10);
});

const esValido = computed(() => {
  if (!tecnicoSeleccionado.value) return false;
  if (carrito.value.length === 0) return false;
  if (!form.codigoSalida || !form.motivo) return false;
  return true;
});

const errorValidacion = computed(() => {
  if (!tecnicoSeleccionado.value) return 'Debes seleccionar un técnico responsable';
  if (carrito.value.length === 0) return 'Debes añadir al menos una herramienta';
  if (!form.codigoSalida) return 'El código de salida es requerido';
  if (!form.motivo) return 'El motivo o destino es requerido';
  return '';
});

// Methods
const cargarDatos = async () => {
  try {
    console.log('Cargando datos de técnicos y herramientas...');
    const [resTecnicos, resTools] = await Promise.all([
      obtenerTecnicos(),
      getHerramientas()
    ]);
    
    console.log('Respuesta Técnicos:', resTecnicos);
    console.log('Respuesta Herramientas:', resTools);
    
    if (resTecnicos && resTecnicos.success) {
      tecnicos.value = resTecnicos.data || [];
    } else if (Array.isArray(resTecnicos)) {
      tecnicos.value = resTecnicos;
    }
    
    herramientas.value = Array.isArray(resTools) ? resTools : (resTools?.data || []);
    
    console.log('Técnicos cargados:', tecnicos.value.length);
    console.log('Herramientas cargadas:', herramientas.value.length);
  } catch (error) {
    console.error('Error al cargar datos iniciales:', error);
    toast('Error al conectar con el servidor', 'error');
  }
};

const ocultarDropdownTecnicos = () => {
  setTimeout(() => {
    mostrarDropdownTecnicos.value = false;
  }, 200);
};

const seleccionarTecnico = (t) => {
  tecnicoSeleccionado.value = t;
  form.idUsuarioRecibe = t.idUsuario;
  buscarTecnico.value = '';
  mostrarDropdownTecnicos.value = false;
};

const agregarAlCarrito = (h) => {
  if (h.stock <= 0) {
    toast('Herramienta sin stock disponible', 'warning');
    return;
  }
  
  const existe = carrito.value.find(item => item.idHerramienta === h.idHerramienta);
  if (existe) {
    if (existe.cantidad < h.stock) {
      existe.cantidad++;
      toast(`Cantidad de ${h.nombre} aumentada`);
    } else {
      toast('No hay más stock disponible', 'warning');
    }
  } else {
    carrito.value.push({
      ...h,
      cantidad: 1
    });
    toast(`${h.nombre} añadida a la lista`);
  }
  buscarHerramienta.value = '';
};

const removerDelCarrito = (index) => {
  carrito.value.splice(index, 1);
};

const aumentarCantidad = (index) => {
  const item = carrito.value[index];
  if (item.cantidad < item.stock) {
    item.cantidad++;
  }
};

const disminuirCantidad = (index) => {
  const item = carrito.value[index];
  if (item.cantidad > 1) {
    item.cantidad--;
  }
};

const procesarSalida = async () => {
  if (!esValido.value) return;
  
  guardando.value = true;
  try {
    const promesas = carrito.value.map((item, index) => {
      const payload = {
        ...form,
        idHerramienta: item.idHerramienta,
        cantidadSalida: item.cantidad,
        codigoSalida: carrito.value.length > 1 ? `${form.codigoSalida}-${index + 1}` : form.codigoSalida
      };
      return crearSalidaHerramienta(payload);
    });

    const resultados = await Promise.all(promesas);
    const fallos = resultados.filter(r => !r.success);

    if (fallos.length === 0) {
      showSuccessModal.value = true;
    } else {
      throw new Error('Algunas salidas no pudieron registrarse');
    }
  } catch (error) {
    console.error('Error al procesar salida:', error);
    toast(error.message || 'Error al guardar el registro', 'error');
  } finally {
    guardando.value = false;
  }
};

const onSuccessModalClose = () => {
  showSuccessModal.value = false;
  router.push('/salida-herramienta');
};

const toast = (message, icon = 'success') => {
  if (toastTimer) clearTimeout(toastTimer);
  toastMessage.value = message;
  toastType.value = icon;
  toastVisible.value = true;
  toastTimer = setTimeout(() => {
    toastVisible.value = false;
  }, 3000);
};

onMounted(cargarDatos);
</script>

<style scoped>
.custom-scrollbar::-webkit-scrollbar {
  width: 4px;
}
.custom-scrollbar::-webkit-scrollbar-track {
  background: transparent;
}
.custom-scrollbar::-webkit-scrollbar-thumb {
  background: #e2e8f0;
  border-radius: 10px;
}
.dark .custom-scrollbar::-webkit-scrollbar-thumb {
  background: #334155;
}
</style>
